import type { FunctionComponentWithChildren } from '@bonsai-components/utility-types';
import { ModIconButton } from '@moderneinc/design-system-components';
import {
  semanticColors,
  semanticTypography,
  spacing
} from '@moderneinc/design-system-tokens';
import {
  Box,
  type BoxProps,
  type Breakpoint,
  Container,
  Fab,
  Fade,
  Stack,
  Typography
} from '@mui/material';
import Head from 'next/head';
import React, { type JSX, type ReactNode, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { DOHERTY_THRESHOLD } from '../../constants/general';
import { useApiExamplesContext } from '../../contexts/api-examples.context';
import { GraphQLIcon, UpArrowIcon } from '../../icons/icons';
import { SeeHowToRunGraphQL } from '../graphql-explorer/see-how-to-run-graphql.component';
import {
  type Breadcrumb,
  BreadcrumbsComponent
} from '../navigation/breadcrumbs/breadcrumb.component';
import { Loading } from '../utilities/loading/loading.component';
import { WithAdministrative } from '../with-administrative/with-administrative.component';
import { StickyBox } from './paper.component';

type PageContentProps = {
  title: string;
  subheading?: ReactNode | string;
  breadcrumbs?: Breadcrumb[];
  pageTitle?: ReactNode | string;
  banner?: ReactNode;
  isLoading: boolean;
  requiresAdministrativeRole?: boolean;
  action?: JSX.Element;
  hideApiExamples?: boolean;
  sticky?: boolean;
  maxWidth?: Breakpoint | false;
  disableGutters?: boolean;
  fullCanvas?: boolean;
} & BoxProps;

/**
 * Simplified page content component designed to work within ModLayout.
 * Handles breadcrumbs, page titles, loading states, and content wrapping.
 *
 * @param fullCanvas - When true, bypasses container constraints for full-canvas layouts like Builder
 */
export const PageContent: FunctionComponentWithChildren<PageContentProps> = ({
  title,
  subheading,
  pageTitle,
  banner,
  isLoading = false,
  requiresAdministrativeRole = false,
  children,
  action = undefined,
  hideApiExamples = false,
  breadcrumbs,
  sticky = false,
  maxWidth = false,
  disableGutters = false,
  fullCanvas = false,
  sx,
  ...boxRest
}) => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const { examples } = useApiExamplesContext();

  const { ref } = useInView({
    threshold: 0.1,
    delay: DOHERTY_THRESHOLD,
    onChange: (inView) => {
      setShowBackToTop(!inView);
    }
  });

  const pageTitleFragment = (
    <Stack
      direction="row"
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        flexGrow: 1,
        width: '100%',
        gap: `${spacing.spacing_2}px`
      }}
    >
      {pageTitle &&
        (typeof pageTitle === 'string' ? (
          <Typography
            component="h1"
            sx={{
              fontSize: semanticTypography.titles.pageHeader.fontSize,
              fontWeight: semanticTypography.titles.pageHeader.fontWeight,
              lineHeight: 1.4,
              mb: 0.5
            }}
          >
            {pageTitle}
          </Typography>
        ) : (
          pageTitle
        ))}
      {/* API Examples - show when available */}
      {!hideApiExamples && examples.length > 0 && (
        <SeeHowToRunGraphQL
          examples={examples}
          slots={{
            button: (
              <ModIconButton
                aria-label="API examples"
                title={
                  examples.length === 1
                    ? `View API example: ${examples.at(0)?.label}`
                    : `View ${examples.length} API examples for this page`
                }
                variant="nav"
              >
                <GraphQLIcon />
              </ModIconButton>
            )
          }}
        />
      )}
    </Stack>
  );

  return (
    <React.Fragment>
      <Head>
        <title>Moderne - {title}</title>
      </Head>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          ...(sx || {})
        }}
        {...boxRest}
      >
        {/* Optional banner slot - rendered without container for full-width */}
        {banner}

        {/* Breadcrumbs + Page title section */}
        {pageTitle ? (
          sticky ? (
            <StickyBox
              id="sticky-header"
              top={0}
              sx={{
                bgcolor: 'inherit',
                backgroundColor: (theme) => theme.palette.background.paper,
                pt: 0.5,
                pb: 0.25,
                px: 3
              }}
            >
              <Stack
                ref={ref}
                sx={{ flexShrink: 0, gap: `${spacing.spacing_1}px` }}
              >
                <BreadcrumbsComponent
                  links={breadcrumbs || [{ label: title }]}
                />
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap'
                  }}
                >
                  <Stack
                    sx={{
                      gap: 1.5,
                      flexGrow: 1
                    }}
                  >
                    {pageTitleFragment}
                    {subheading &&
                      (React.isValidElement(subheading) ? (
                        subheading
                      ) : (
                        <Typography variant="caption">{subheading}</Typography>
                      ))}
                  </Stack>
                  {action}
                </Stack>
              </Stack>
            </StickyBox>
          ) : (
            <Box
              ref={ref}
              sx={{
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: `${spacing.spacing_1}px`,
                // Drop the title gutter for full-canvas pages so it aligns with
                // the gutterless content below (e.g. the builder canvas).
                px: disableGutters ? 0 : `${spacing.spacing_2_1_2}px`,
                pt: `${spacing.spacing_2}px`,
                pb: `${spacing.spacing_1}px`
              }}
            >
              <BreadcrumbsComponent links={breadcrumbs || [{ label: title }]} />
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: `${spacing.spacing_2}px`
                }}
              >
                <Stack sx={{ gap: `${spacing.spacing_1}px`, flexGrow: 1 }}>
                  {pageTitleFragment}
                  {subheading &&
                    (React.isValidElement(subheading) ? (
                      subheading
                    ) : (
                      <Typography variant="caption">{subheading}</Typography>
                    ))}
                </Stack>
                {action}
              </Stack>
            </Box>
          )
        ) : (
          <Box
            ref={ref}
            sx={{
              px: `${spacing.spacing_2_1_2}px`,
              pt: `${spacing.spacing_2}px`
            }}
          >
            <BreadcrumbsComponent links={breadcrumbs || [{ label: title }]} />
          </Box>
        )}

        {/* Main content area */}
        {fullCanvas ? (
          /* Full canvas mode - no container constraints */
          requiresAdministrativeRole ? (
            <WithAdministrative>
              <LoadingAndThenDisplay
                loading={isLoading}
                fullCanvas={fullCanvas}
              >
                {children}
              </LoadingAndThenDisplay>
            </WithAdministrative>
          ) : (
            <LoadingAndThenDisplay loading={isLoading} fullCanvas={fullCanvas}>
              {children}
            </LoadingAndThenDisplay>
          )
        ) : (
          /* Standard mode with container */
          <Container
            maxWidth={typeof maxWidth === 'boolean' ? false : maxWidth}
            disableGutters={disableGutters}
            sx={{ margin: 'unset' }}
          >
            {/* Check for administrative restrictions */}
            {requiresAdministrativeRole ? (
              <WithAdministrative>
                <LoadingAndThenDisplay loading={isLoading}>
                  {children}
                </LoadingAndThenDisplay>
              </WithAdministrative>
            ) : (
              <LoadingAndThenDisplay loading={isLoading}>
                {children}
              </LoadingAndThenDisplay>
            )}
          </Container>
        )}
      </Box>
      {/* Back to top button */}
      <Fade in={showBackToTop}>
        <Box
          sx={{
            position: 'fixed',
            bottom: (theme) => theme.spacing(2),
            right: (theme) => theme.spacing(4),
            zIndex: 1000
          }}
        >
          <Fab
            sx={{
              backgroundColor: semanticColors.color.surface.highlight,
              color: semanticColors.color.text.link
            }}
            title="Back to top"
            size="small"
            onClick={() => {
              // Try to find the scrollable content container created by Layout component
              const scrollContainer = document.getElementById(
                'moderne-content-scroll-container'
              );
              if (scrollContainer) {
                scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
              } else {
                // Fallback to window scroll if container not found
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            <UpArrowIcon />
          </Fab>
        </Box>
      </Fade>
    </React.Fragment>
  );
};

const LoadingAndThenDisplay: FunctionComponentWithChildren<{
  loading: boolean;
  fullCanvas?: boolean;
}> = ({ loading, fullCanvas = false, children }) => {
  return loading ? (
    <Stack
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh'
      }}
    >
      <Loading caption="Loading" wait={DOHERTY_THRESHOLD} />
    </Stack>
  ) : fullCanvas ? (
    <Box
      id="page-content-container"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0
      }}
    >
      {children}
    </Box>
  ) : (
    <Box
      id="page-content-container"
      sx={{
        margin: '0 0 .5rem 0',
        padding: 0,
        maxWidth: '100%'
      }}
    >
      {children}
    </Box>
  );
};
